import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { api, type ApiError, type OpenPublicPageResult } from '@whinerp/client';
import { BlockDocumentView, Button } from '@whinerp/ui';
import type { Block } from '@whinerp/contracts/blocks';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { blockEditorLabels } from '../marketing/block-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-64 A hosted landing page (F-072).
 *
 * `/p/<workspace>/<page>` for what is live, and `/p/preview/<token>` for a
 * private draft - the preview says on the page that it is not live.
 *
 * **UTM parameters are captured without a field.** Whatever came in the
 * address - utm_*, gclid - goes with the submission, so the touch that
 * brought somebody here is recorded without a hidden input anybody could
 * forget to add.
 *
 * **Progressive profiling.** A submission hands back a visitor token, kept in
 * this browser; the next page asks only for what is not on file.
 */

type Page = NonNullable<OpenPublicPageResult['page']>;

const visitorKey = (workspace: string) => `whinerp.visitor.${workspace}`;
function readVisitor(workspace: string): string | null {
  try {
    return window.localStorage.getItem(visitorKey(workspace));
  } catch {
    return null;
  }
}
function keepVisitor(workspace: string, token: string): void {
  try {
    window.localStorage.setItem(visitorKey(workspace), token);
  } catch {
    // A browser that keeps nothing asks for everything next time. Correct, just longer.
  }
}

/** What came in the address, as it came. */
function trackingParameters(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.startsWith('utm_') || key === 'gclid' || key === 'fbclid') out[key] = value;
  }
  return out;
}

function LandingPageScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const params = useParams();
  // The router's address, not window.location: the app's routing is the one
  // truth about what URL this page was opened with.
  const location = useLocation();
  const workspace = params['workspace'] ?? '';
  const slug = params['slug'] ?? '';
  const previewing = workspace === 'preview';
  const opened = useRef(Date.now());
  const [values, setValues] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);

  const page = useQuery<OpenPublicPageResult, ApiError>({
    queryKey: ['public-page', workspace, slug],
    queryFn: () =>
      previewing
        ? api.openPagePreview({ body: { token: slug } })
        : api.openPublicPage({
            path: { workspace, slug },
            query: readVisitor(workspace) ? { visitor: readVisitor(workspace) ?? '' } : {},
          }),
    retry: false,
  });

  useEffect(() => {
    const data = page.data?.page;
    if (!data) return;
    document.title = data.title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', data.description ?? '');
  }, [page.data]);

  const submit = useMutation({
    mutationFn: () =>
      api.submitPageForm({
        path: { workspace, slug },
        body: {
          values,
          consent,
          utm: trackingParameters(location.search),
          referrer: document.referrer || null,
          filledInSeconds: Math.round((Date.now() - opened.current) / 1000),
          visitor: readVisitor(workspace),
        },
      }),
    onSuccess: (result) => {
      if (result.visitor) keepVisitor(workspace, result.visitor);
    },
  });

  if (page.isPending) {
    return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
  }
  if (page.isError || !page.data.page) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-2 px-gutter py-6">
        <h1 data-landing-missing className="text-h2 text-ink">{t('landing.missing')}</h1>
        {page.isError ? <p className="text-body text-ink-meta">{errorText(page.error)}</p> : null}
      </main>
    );
  }
  const data: Page = page.data.page;

  return (
    <main data-component="LandingPage" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-6 px-gutter py-6">
      {data.preview ? (
        <p data-landing-preview className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
          {t('landing.previewBanner')}
        </p>
      ) : null}
      <h1 className="text-h1 text-ink">{data.title}</h1>
      <BlockDocumentView blocks={data.blocks as Block[]} labels={blockEditorLabels(t)} live />

      {data.form ? (
        <section data-landing-form className="flex flex-col gap-3 rounded-card border border-rule p-4">
          {submit.data?.accepted ? (
            <p data-landing-thanks className="text-body text-ink">{submit.data.message}</p>
          ) : (
            <>
              {data.form.alreadyKnown > 0 ? (
                <p data-landing-known className="text-meta text-ink-meta">{t('landing.welcomeBack')}</p>
              ) : null}
              {data.form.fields.map((field) => (
                <label key={field.field} className="flex flex-col gap-1 text-dense text-ink-600">
                  {field.label}
                  <input
                    data-landing-field={field.field}
                    required={field.required}
                    type={field.field === 'email' ? 'email' : 'text'}
                    className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink"
                    value={values[field.field] ?? ''}
                    onChange={(event) => setValues((current) => ({ ...current, [field.field]: event.target.value }))}
                  />
                </label>
              ))}
              {data.form.consentRequired && data.form.consentText ? (
                <label className="flex items-start gap-2 text-dense text-ink">
                  <input type="checkbox" data-landing-consent checked={consent} onChange={(event) => setConsent(event.target.checked)} />
                  <span>{data.form.consentText}</span>
                </label>
              ) : null}
              {submit.data && !submit.data.accepted ? (
                <p data-landing-refusal className="text-dense text-danger-text">
                  {submit.data.messageKey ? errorText({ message: submit.data.message, messageKey: submit.data.messageKey }) : submit.data.message}
                </p>
              ) : null}
              {data.preview ? (
                <p className="text-meta text-ink-meta">{t('landing.previewNoSubmit')}</p>
              ) : (
                <div>
                  <Button intent="primary" data-landing-submit disabled={submit.isPending} onClick={() => submit.mutate()}>
                    {t('landing.submit')}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      ) : null}
    </main>
  );
}

function LandingEmpty(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-ink-meta">{t('landing.missing')}</p>;
}
function LandingLoading(): JSX.Element {
  const t = useT();
  return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
}
function LandingError(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-danger-text">{t('landing.missing')}</p>;
}

export const screen = defineScreen({
  id: 'SC-64',
  route: '/p/:workspace/:slug',
  surface: 'page',
  chrome: 'bare',
  title: 'Landing page',
  features: ['F-072'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: [],
  elements: ['LandingPage', 'BlockDocumentView'],
  // The workspace's own slug and its first page - the seeded live one - rather
  // than a literal address that exists only in the demo database.
  sampleRecord: [
    { table: { name: 'tenant', idColumn: 'slug' }, param: 'workspace' },
    { table: { name: 'landing_page', idColumn: 'slug' }, param: 'slug' },
  ],
  states: { empty: LandingEmpty, loading: LandingLoading, error: LandingError },
  component: LandingPageScreen,
});

export default screen;
